import React, { Component } from "react";
import {
  Form, Input, Button, Select, Upload, Rate, notification, Spin,
} from "antd";
import { UploadOutlined, RobotOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

const NAVY  = "#1A2280";
const GOLD  = "#D4921A";
const BORDER = "#E2E8F0";
const MUTED = "#718096";
const LIGHT = "#F7F9FC";

// ── Rating options ──────────────────────────────────────────────────────────
const RATINGS = [
  { value: "excellent",   label: "⭐⭐⭐⭐⭐  Excellent — no issues" },
  { value: "good",        label: "⭐⭐⭐⭐  Good — minor wear" },
  { value: "fair",        label: "⭐⭐⭐  Fair — needs attention soon" },
  { value: "poor",        label: "⭐⭐  Poor — needs repair/replace" },
  { value: "critical",    label: "⭐  Critical — urgent action needed" },
  { value: "unknown",     label: "❓  Unknown / Not inspected" },
];

// ── AI proxy — routes through Netlify function to avoid CORS ────────────────
const AI_PROXY = "https://ai.myhomebuddha.com/.netlify/functions/claude";
const AI_MODEL = "claude-haiku-4-5-20251001";

class ChecklistForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      description:    "",
      notes:          "",
      brand:          "",
      model:          "",
      year:           "",
      rating:         undefined,
      fileList:       [],
      analyzing:      false,
      analysisResult: "",
      editingId:      null,
      expandedEntries: {},  // { [entryId]: bool }
    };
    this.formRef = React.createRef();
  }

  resetForm = () => {
    this.setState({
      description: "", notes: "", brand: "", model: "", year: "",
      rating: undefined, fileList: [], analyzing: false, analysisResult: "", editingId: null,
    });
    if (this.formRef.current) this.formRef.current.resetFields();
  };

  handleEdit = (entry) => {
    // Load all fields back into state (which drives the controlled inputs)
    this.setState({
      description:    entry.description    || "",
      notes:          entry.notes          || "",
      brand:          entry.brand          || "",
      model:          entry.model          || "",
      year:           entry.year           || "",
      rating:         entry.rating         || undefined,
      analysisResult: entry.analysisResult || "",
      editingId:      entry.id,
      fileList:       entry.photos         || [],
    });
  };

  handleSubmit = () => {
    const { description, notes, brand, model, year, rating, fileList, editingId, analysisResult } = this.state;
    this.props.onSectionSubmit(
      { description, notes, brand, model, year, rating, photos: fileList, analysisResult },
      editingId
    );
    this.resetForm();
  };

  handleDelete = (id) => {
    if (this.props.onSectionDelete) this.props.onSectionDelete(id);
  };

  // ── Analyze photos via Netlify proxy (avoids CORS) ─────────────────────────
  analyzePhotos = async () => {
    const { fileList } = this.state;
    const { title } = this.props;

    this.setState({ analyzing: true, analysisResult: "" });

    try {
      const imageMessages = [];

      for (const file of fileList) {
        try {
          if (file.originFileObj) {
            // New upload — read directly from file object
            const base64 = await new Promise((res, rej) => {
              const reader = new FileReader();
              reader.onload = () => res(reader.result.split(",")[1]);
              reader.onerror = rej;
              reader.readAsDataURL(file.originFileObj);
            });
            const mimeType = file.originFileObj.type || "image/jpeg";
            imageMessages.push({ type: "image", source: { type: "base64", media_type: mimeType, data: base64 } });
          } else if (file.url) {
            // Already-saved photo — fetch URL and convert to base64
            // (Claude API doesn't accept arbitrary S3 URLs directly)
            const resp = await fetch(file.url);
            const blob = await resp.blob();
            const base64 = await new Promise((res, rej) => {
              const reader = new FileReader();
              reader.onload = () => res(reader.result.split(",")[1]);
              reader.onerror = rej;
              reader.readAsDataURL(blob);
            });
            const mimeType = blob.type || "image/jpeg";
            imageMessages.push({ type: "image", source: { type: "base64", media_type: mimeType, data: base64 } });
          }
        } catch (fileErr) {
          console.warn("Could not load photo for analysis:", file.name || file.url, fileErr);
        }
      }

      if (imageMessages.length === 0) {
        notification.warning({ message: "Please upload photos first" });
        this.setState({ analyzing: false });
        return;
      }

      imageMessages.push({
        type: "text",
        text: "You are a home inspection AI. Analyze these photos of the " + title + " area/system. " +
          "Identify: 1) Brand/manufacturer if visible on labels, 2) Model number if visible, " +
          "3) Approximate age/year if install date or manufacture date is shown, " +
          "4) Visible condition (excellent/good/fair/poor/critical), " +
          "5) Any concerns or issues visible. " +
          "Be specific and practical. Format your response clearly with these sections."
      });

      // ── Route through Netlify proxy — not direct to Anthropic (CORS) ──────
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: AI_MODEL,
          max_tokens: 600,
          messages: [{ role: "user", content: imageMessages }],
        }),
      });

      const data = await response.json();
      const result = data.content?.[0]?.text || "Could not analyze photos.";
      this.setState({ analyzing: false, analysisResult: result });

      // Auto-fill brand/model/year — handles markdown AI output format:
      // "## 1) Brand/Manufacturer\n**Rheem Sales Company, Inc.**"
      // "## 2) Model Number\n**RBHP-17J06NFD**" 
      // Also handles "Brand: Carrier" inline style
      const extractField = (text, ...keywords) => {
        for (const kw of keywords) {
          // Markdown section header style: ## Brand\n**Value** or ## Brand\nValue
          const hdrMatch = text.match(new RegExp(kw + '[^\\n]*\\n\\*{0,2}([^\\n*]{2,50})\\*{0,2}', 'i'));
          if (hdrMatch?.[1]?.trim()) return hdrMatch[1].trim();
          // Inline colon style: Brand: Rheem or Brand/Manufacturer: Rheem
          const inlineMatch = text.match(new RegExp(kw + '[^:\\n]*:\\s*\\*{0,2}([A-Za-z0-9][^\\n*,]{1,40})\\*{0,2}', 'i'));
          if (inlineMatch?.[1]?.trim()) return inlineMatch[1].trim();
        }
        return null;
      };

      const rawBrand = extractField(result, 'Brand', 'Manufacturer');
      const rawModel = extractField(result, 'Model');
      const yearMatch = result.match(/((?:19|20)\d{2})/);

      // Clean up: remove parenthetical notes like "(Fort Smith, Arkansas)"
      const cleanBrand = rawBrand ? rawBrand.replace(/\s*\(.*?\)/, '').replace(/\*+/g, '').trim() : null;
      const cleanModel = rawModel ? rawModel.replace(/\*+/g, '').trim() : null;

      const updates = {};
      if (cleanBrand && !this.state.brand) updates.brand = cleanBrand;
      if (cleanModel && !this.state.model) updates.model = cleanModel;
      if (yearMatch?.[1] && !this.state.year) updates.year = yearMatch[1];

      // setState drives the controlled inputs directly — no need for setFieldsValue
      if (Object.keys(updates).length > 0) {
        this.setState(updates);
      }

    } catch (err) {
      console.error("analyzePhotos error:", err);
      this.setState({ analyzing: false });
      notification.error({ message: "Analysis failed — please try again" });
    }
  };

  render() {
    const { title, points, submittedData = [] } = this.props;
    const { description, notes, brand, model, year, rating, fileList, analyzing, analysisResult, editingId } = this.state;

    return (
      <div>
        {/* ── Points to Review ─────────────────────────────────────────── */}
        {points?.length > 0 && (
          <div style={{
            background: LIGHT, borderRadius: "10px", padding: "12px 14px",
            marginBottom: "16px", border: "1px solid " + BORDER,
          }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "12px", marginBottom: "8px" }}>
              📋 Points to Review
            </div>
            <ol style={{ margin: 0, paddingLeft: "18px" }}>
              {points.map((pt, i) => (
                <li key={i} style={{ fontSize: "12px", color: "#2D3748", marginBottom: "4px", lineHeight: 1.5 }}>
                  {pt}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* ── Form ─────────────────────────────────────────────────────── */}
        <Form ref={this.formRef} layout="vertical" size="middle">
          <Form.Item label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Description</span>}
            name="description">
            <TextArea rows={3} placeholder="Summarize observations..."
              value={description}
              onChange={e => this.setState({ description: e.target.value })}
              style={{ borderRadius: "8px", fontSize: "12px" }} />
          </Form.Item>

          <Form.Item label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Additional Notes</span>}
            name="notes">
            <TextArea rows={2} placeholder="Extra comments, concerns..."
              value={notes}
              onChange={e => this.setState({ notes: e.target.value })}
              style={{ borderRadius: "8px", fontSize: "12px" }} />
          </Form.Item>

          <div style={{ display: "flex", gap: "10px" }}>
            <Form.Item
              label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Brand <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span></span>}
              name="brand" style={{ flex: 1 }}>
              <Input placeholder="e.g. Carrier, Rheem..."
                value={brand}
                onChange={e => this.setState({ brand: e.target.value })}
                style={{ borderRadius: "8px", fontSize: "12px" }} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Model <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span></span>}
              name="model" style={{ flex: 1 }}>
              <Input placeholder="Model number..."
                value={model}
                onChange={e => this.setState({ model: e.target.value })}
                style={{ borderRadius: "8px", fontSize: "12px" }} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Year/Age <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span></span>}
              name="year" style={{ flex: 1 }}>
              <Input placeholder="e.g. 2018"
                value={year}
                onChange={e => this.setState({ year: e.target.value })}
                style={{ borderRadius: "8px", fontSize: "12px" }} />
            </Form.Item>
          </div>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Overall Rating <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span></span>}
            name="rating">
            <Select placeholder="Select condition..."
              value={rating}
              onChange={v => this.setState({ rating: v })}
              style={{ borderRadius: "8px" }}
              getPopupContainer={t => t.parentNode}>
              {RATINGS.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select>
          </Form.Item>

          {/* Photos + Analyze button */}
          <Form.Item label={<span style={{ fontWeight: 600, fontSize: "12px", color: NAVY }}>Photos</span>}>
            <Upload
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: fl }) => this.setState({ fileList: fl })}
              multiple
              accept="image/*">
              <Button icon={<UploadOutlined />} style={{ borderRadius: "8px", fontSize: "12px" }}>
                Upload Photos (max 10)
              </Button>
            </Upload>

            {/* Analyze photos button — shows when files are uploaded */}
            {fileList.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  loading={analyzing}
                  onClick={this.analyzePhotos}
                  style={{
                    borderRadius: "50px", fontWeight: 700,
                    background: NAVY, borderColor: NAVY,
                    fontSize: "12px",
                  }}>
                  {analyzing ? "Analyzing photos..." : "🔍 Analyze Photos — Auto-fill Brand, Model & Year"}
                </Button>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>
                  AI reads manufacturer labels to extract brand, model & install year
                </div>
              </div>
            )}
          </Form.Item>

          {/* Analysis result */}
          {analysisResult && (
            <div style={{
              background: "#EBF4FF", borderRadius: "10px", padding: "12px 14px",
              marginBottom: "14px", border: "1px solid #BEE3F8",
            }}>
              <div style={{ fontWeight: 700, color: "#2B6CB0", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>
                🤖 Photo Analysis
              </div>
              <div style={{ fontSize: "12px", color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-line" }}>
                {analysisResult}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              type="primary"
              onClick={this.handleSubmit}
              style={{
                borderRadius: "50px", background: NAVY, borderColor: NAVY,
                fontWeight: 700, fontSize: "13px", paddingLeft: "20px", paddingRight: "20px",
              }}>
              {editingId ? "Update Entry" : "Save Entry"}
            </Button>
            {editingId && (
              <Button onClick={this.resetForm}
                style={{ borderRadius: "50px", fontSize: "13px" }}>
                Cancel
              </Button>
            )}
          </div>
        </Form>

        {/* ── Submitted entries ─────────────────────────────────────────── */}
        {submittedData.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "12px", marginBottom: "8px", textTransform: "uppercase", letterSpacing: ".04em" }}>
              Saved Entries
            </div>
            {submittedData.map((entry, idx) => (
              <div key={entry.id || idx} style={{
                background: LIGHT, borderRadius: "10px",
                marginBottom: "8px", border: "1px solid " + BORDER, overflow: "hidden",
              }}>
                {/* ── Collapsed header — always visible, click to expand ── */}
                <div
                  onClick={() => this.setState(s => ({ expandedEntries: { ...s.expandedEntries, [entry.id || idx]: !s.expandedEntries[entry.id || idx] } }))}
                  style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      {entry.year  && <span style={{ fontSize: "11px", background: "#EBF4FF", color: "#2B6CB0", borderRadius: "50px", padding: "1px 8px", fontWeight: 700 }}>📅 {entry.year}</span>}
                      {entry.brand && <span style={{ fontSize: "11px", color: MUTED }}>🏷 {entry.brand}</span>}
                      {entry.model && <span style={{ fontSize: "11px", color: MUTED }}>📋 {entry.model}</span>}
                      {entry.analysisResult && <span style={{ fontSize: "10px", background: "#EBF4FF", color: "#2B6CB0", borderRadius: "50px", padding: "1px 8px", fontWeight: 700 }}>🤖 AI</span>}
                      {entry.photos?.length > 0 && <span style={{ fontSize: "10px", color: MUTED }}>📷 {entry.photos.length}</span>}
                      {!entry.year && !entry.brand && !entry.model && entry.description && (
                        <span style={{ fontSize: "11px", color: "#2D3748" }}>{entry.description.slice(0, 60)}{entry.description.length > 60 ? "..." : ""}</span>
                      )}
                    </div>
                  </div>
                  <span style={{ color: MUTED, fontSize: "16px", transition: "transform .2s", transform: this.state.expandedEntries[entry.id || idx] ? "rotate(180deg)" : "none" }}>⌄</span>
                </div>

                {/* ── Expanded detail ────────────────────────────────────── */}
                {this.state.expandedEntries[entry.id || idx] && (
                  <div style={{ borderTop: "1px solid " + BORDER, padding: "12px 14px" }}>
                    {entry.description && (
                      <div style={{ fontSize: "12px", color: "#2D3748", marginBottom: "8px", lineHeight: 1.6 }}>
                        {entry.description}
                      </div>
                    )}
                    {entry.notes && (
                      <div style={{ fontSize: "12px", color: MUTED, marginBottom: "8px", lineHeight: 1.6, fontStyle: "italic" }}>
                        {entry.notes}
                      </div>
                    )}
                    {entry.rating && (
                      <div style={{ fontSize: "11px", color: MUTED, marginBottom: "8px" }}>
                        {RATINGS.find(r => r.value === entry.rating)?.label || entry.rating}
                      </div>
                    )}
                    {entry.analysisResult && (
                      <div style={{ background: "#EBF4FF", borderRadius: "8px", padding: "10px 12px", marginBottom: "10px", border: "1px solid #BEE3F8" }}>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#2B6CB0", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>🤖 AI Analysis</div>
                        <div style={{ fontSize: "12px", color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-line" }}>
                          {entry.analysisResult}
                        </div>
                      </div>
                    )}
                    {entry.photos?.length > 0 && (
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
                        {entry.photos.map((photo, pi) => (
                          photo.url && <img key={pi} src={photo.url} alt=""
                            style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "6px", border: "1px solid " + BORDER }} />
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => this.handleEdit(entry)}
                        style={{ fontSize: "11px", color: NAVY, background: "none", border: "1px solid " + NAVY, borderRadius: "50px", padding: "3px 12px", cursor: "pointer" }}>
                        Edit
                      </button>
                      <button onClick={() => this.handleDelete(entry.id)}
                        style={{ fontSize: "11px", color: "#E53E3E", background: "none", border: "1px solid #E53E3E", borderRadius: "50px", padding: "3px 12px", cursor: "pointer" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default ChecklistForm;